import { ConversationInfo, SessionType } from '@/api/types/chat'
import React, { useRef, useImperativeHandle, forwardRef, useEffect, useState, useMemo } from 'react'
import { formatChatListTime } from '@/utils/date-utils'
import ChatContextMenu from './_components/ChatContextMenu'
import { useChatActions } from '@/hooks/useChatActions'

interface ChatListProps {
  conversations: ConversationInfo[]
  activeConversationId: string
  onChatSelect: (conversationId: string) => void
  isLoading?: boolean
}

export interface ChatListRef {
  scrollToConversation: (conversationId: string) => void
}

const ChatList = forwardRef<ChatListRef, ChatListProps>(({
  conversations,
  activeConversationId,
  onChatSelect,
  isLoading = false,
}, ref): React.JSX.Element => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const conversationRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [searchTerm, setSearchTerm] = useState('')

  // 右键菜单状态
  const [contextMenu, setContextMenu] = useState<{
    isVisible: boolean
    x: number
    y: number
    conversationId: string | null
  }>({
    isVisible: false,
    x: 0,
    y: 0,
    conversationId: null
  })

  // 聊天操作hooks
  const { pinConversation, unpinConversation, muteConversation, unmuteConversation } = useChatActions()

  // 过滤后的对话列表
  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) {
      return conversations
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim()
    return conversations.filter(conversation => {
      // 搜索对话标题
      const titleMatch = conversation.title?.toLowerCase().includes(lowerSearchTerm)
      // 搜索对话ID
      const idMatch = conversation.id.toLowerCase().includes(lowerSearchTerm)

      return titleMatch || idMatch
    })
  }, [conversations, searchTerm])

  // 清空搜索
  const clearSearch = () => {
    setSearchTerm('')
  }

  // 右键菜单处理
  const handleContextMenu = (e: React.MouseEvent, conversationId: string) => {
    e.preventDefault()
    e.stopPropagation()

    setContextMenu({
      isVisible: true,
      x: e.clientX,
      y: e.clientY,
      conversationId
    })
  }

  // 关闭右键菜单
  const closeContextMenu = () => {
    setContextMenu({
      isVisible: false,
      x: 0,
      y: 0,
      conversationId: null
    })
  }

  // 获取当前右键菜单对应的对话
  const getContextMenuConversation = () => {
    if (!contextMenu.conversationId) return null
    return conversations.find(conv => conv.id === contextMenu.conversationId)
  }

  // 置顶/取消置顶处理
  const handlePin = async () => {
    if (!contextMenu.conversationId) return

    const conversation = getContextMenuConversation()
    if (!conversation) return

    if (conversation.is_pinned) {
      await unpinConversation(contextMenu.conversationId)
    } else {
      await pinConversation(contextMenu.conversationId)
    }
  }

  // 静音/取消静音处理
  const handleMute = async () => {
    if (!contextMenu.conversationId) return

    const conversation = getContextMenuConversation()
    if (!conversation) return

    if (conversation.is_muted) {
      await unmuteConversation(contextMenu.conversationId)
    } else {
      await muteConversation(contextMenu.conversationId)
    }
  }

  // 滚动到指定对话
  const scrollToConversation = (conversationId: string) => {
    const conversationElement = conversationRefs.current[conversationId]
    if (conversationElement && scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const elementTop = conversationElement.offsetTop
      const elementHeight = conversationElement.offsetHeight
      const containerHeight = container.clientHeight
      const scrollTop = container.scrollTop

      // 检查元素是否在视窗内
      const isVisible = elementTop >= scrollTop && elementTop + elementHeight <= scrollTop + containerHeight

      if (!isVisible) {
        // 滚动到元素位置，居中显示
        const targetScrollTop = elementTop - (containerHeight - elementHeight) / 2
        container.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        })
      }
    }
  }

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    scrollToConversation
  }))

  // 当活跃对话改变时自动滚动
  useEffect(() => {
    if (activeConversationId) {
      // 延迟一下确保DOM更新完成
      setTimeout(() => {
        scrollToConversation(activeConversationId)
      }, 100)
    }
  }, [activeConversationId])

  return (
    <div className="w-80 overflow-hidden bg-white border-r border-gray-200 flex flex-col">
      {/* 头部搜索 */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <input
            type="text"
            placeholder="搜索对话..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-10 py-2 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
          />
          <svg className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute right-2.5 top-2.5 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* 聊天列表 */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        {isLoading && conversations.length === 0 ? (
          // 加载状态
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredConversations.length === 0 ? (
          // 空状态
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {searchTerm ? (
                    // 搜索无结果图标
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  ) : (
                    // 无对话图标
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  )}
                </svg>
              </div>
              <p className="text-gray-500 text-sm">
                {searchTerm ? `未找到包含"${searchTerm}"的对话` : '暂无对话'}
              </p>
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="mt-2 text-blue-500 hover:text-blue-600 text-sm underline"
                >
                  清空搜索
                </button>
              )}
            </div>
          </div>
        ) : (
          // 对话列表
          filteredConversations.map((conversation) => (
            <div
              key={conversation.id}
              ref={(el) => {
                conversationRefs.current[conversation.id] = el
              }}
              onClick={() => onChatSelect(conversation.id)}
              onContextMenu={(e) => handleContextMenu(e, conversation.id)}
              className={`p-4 cursor-pointer transition-colors border-l-2 border-b border-gray-50 
               ${activeConversationId === conversation.id
                  ? 'bg-blue-100 border-l-blue-600'
                  : conversation.is_pinned ? 'bg-gray-100' : 'border-l-transparent hover:bg-gray-50'
                } `}
            >
              <div className="flex items-center space-x-3 ">
                {/* 头像 */}
                <div className="relative flex-shrink-0">
                  {conversation.avatar ? (
                    <img
                      src={conversation.avatar}
                      alt={conversation.title || '对话'}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    // 默认头像
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-white font-medium text-lg">
                        {conversation.title ? conversation.title.charAt(0).toUpperCase() : '?'}
                      </span>
                    </div>
                  )}

                  {/* 在线状态指示器（仅私聊显示） */}
                  {/* {conversation.session_type === SessionType.Direct && (
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gray-400 rounded-full border-2 border-white"></div>
                  )} */}

                  {/* 群组成员数量指示器 */}
                  {/* {conversation.session_type === SessionType.Group && (
                    <div className="absolute -bottom-1 -right-1 bg-gray-600 text-white text-xs rounded-full px-1 min-w-4 h-4 flex items-center justify-center border-2 border-white">
                      {conversation.participants?.length || 0}
                    </div>
                  )} */}
                </div>

                {/* 对话信息 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="w-full flex flex-row items-center space-x-2">
                      <h3 className="shrink-1 text-sm font-medium text-gray-900 truncate">
                        {conversation.title || '未知对话'}
                      </h3>
                      {conversation.session_type === SessionType.Group && (
                        <span className="shrink-0 mr-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          群聊
                        </span>
                      )}
                      {conversation.is_pinned && (
                        <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M16 12V4a1 1 0 00-1-1H9a1 1 0 00-1 1v8H6l3 3h2v5a1 1 0 001 1h0a1 1 0 001-1v-5h2l3-3h-2z" />
                        </svg>
                      )}
                    </div>

                  </div>

                  <div className="flex items-center justify-between mt-1">
                    <p className="text-sm text-gray-500 truncate">
                      {/* 这里可以显示最后一条消息，目前使用占位符 */}
                      {conversation.session_type === SessionType.Group ? '群聊消息' : '私聊消息'}
                    </p>
                  </div>

                  {/* 对话状态指示器 */}
                  <div className="flex items-center mt-1 space-x-2">
                    {conversation.is_muted && (
                      <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.788L4.828 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.828l3.555-3.788zM13 8.414l1.415-1.415a1 1 0 011.414 1.414L14.414 10l1.415 1.415a1 1 0 11-1.414 1.414L13 11.414l-1.415 1.415a1 1 0 11-1.414-1.414L11.586 10 10.17 8.585a1 1 0 011.414-1.414L13 8.414z" clipRule="evenodd" />
                      </svg>
                    )}

                    {conversation.is_archived && (
                      <span className="text-xs text-gray-400">已归档</span>
                    )}
                  </div>
                </div>

                <div className='shrink-0 flex flex-col items-end space-y-1'>
                  {conversation.isAtSelf && (
                    <span className="inline-flex items-center justify-center p-1 text-xs font-bold leading-none text-red-500 min-w-5 h-5">
                      @
                    </span>
                  )}

                  {/* 未读消息数量显示 */}
                  {
                    conversation.unreadCount && conversation.unreadCount > 0 ? (
                      <span className="inline-flex items-center justify-center px-2 py-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full min-w-5 h-5">
                        {conversation.unreadCount}
                      </span>
                    ) : <></>
                  }

                  <span className="text-xs text-gray-500">
                    {conversation.last_read_at ? formatChatListTime(conversation.last_read_at) : ''}
                  </span>
                </div>

              </div>
            </div>
          ))
        )}
      </div>

      {/* 右键菜单 */}
      <ChatContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        isVisible={contextMenu.isVisible}
        isPinned={getContextMenuConversation()?.is_pinned || false}
        isMuted={getContextMenuConversation()?.is_muted || false}
        onClose={closeContextMenu}
        onPin={handlePin}
        onUnpin={handlePin}
        onMute={handleMute}
        onUnmute={handleMute}
      />
    </div>
  )
})

ChatList.displayName = 'ChatList'

export default ChatList
